import { useState } from 'react';
import { getSessionId } from './session.js';

/**
 * Modal form for submitting a new Discord server. Posts to /api/submit, which
 * verifies the invite and queues it as 'pending' for maintainer approval.
 * `courses` is the raw course list (courseId + courseName) for the dropdown.
 * Users can also propose a course that isn't in the list yet (still reviewed).
 */
export default function AddServerModal({ courses, onClose }) {
  const [courseId, setCourseId] = useState('');
  const [isNewCourse, setIsNewCourse] = useState(false);
  const [newCourseId, setNewCourseId] = useState('');
  const [newCourseName, setNewCourseName] = useState('');
  const [professorName, setProfessorName] = useState('');
  const [inviteUrl, setInviteUrl] = useState('');
  const [state, setState] = useState({ status: 'idle', message: '' });

  const sortedCourses = [...courses].sort((a, b) => a.courseId - b.courseId);

  const onCourseChange = (value) => {
    if (value === '__new__') {
      setIsNewCourse(true);
      setCourseId('__new__');
    } else {
      setIsNewCourse(false);
      setCourseId(value);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setState({ status: 'submitting', message: '' });

    const payload = {
      professorName,
      inviteUrl,
      sessionId: getSessionId(),
    };

    if (isNewCourse) {
      payload.newCourse = true;
      payload.courseId = Number(newCourseId);
      payload.courseName = newCourseName;
    } else {
      payload.courseId = Number(courseId);
    }

    try {
      const res = await fetch('/api/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setState({
          status: 'success',
          message: `Thanks! “${data.serverName || 'Your server'}” has been sent to admin review.`,
        });
      } else {
        setState({ status: 'error', message: data.error || 'Something went wrong. Please try again.' });
      }
    } catch {
      setState({
        status: 'error',
        message: 'Could not reach the server. (Locally, run `vercel dev` — `npm start` has no API.)',
      });
    }
  };

  return (
    <div className="stats-popup" onClick={(e) => e.stopPropagation()}>
      <div className="stats-popup-content">
        <div className="stats-header">
          <h3>Add a Discord server</h3>
          <button type="button" className="close-button" onClick={onClose}>
            ✕
          </button>
        </div>

        {state.status === 'success' ? (
          <div className="add-server-success">
            <p>{state.message}</p>
            <p className="add-server-note">
              It will appear on the site once an admin approves it.
            </p>
            <button type="button" className="cta-button" onClick={onClose}>
              Done
            </button>
          </div>
        ) : (
          <form className="add-server-form" onSubmit={submit}>
            <label>
              Course
              <select
                value={courseId}
                onChange={(e) => onCourseChange(e.target.value)}
                required
              >
                <option value="" disabled>
                  Select a course…
                </option>
                {sortedCourses.map((c) => (
                  <option key={c.courseId} value={c.courseId}>
                    COSC {c.courseId} — {c.courseName}
                  </option>
                ))}
                <option value="__new__">Course not listed…</option>
              </select>
            </label>

            {isNewCourse && (
              <>
                <label>
                  Course number
                  <input
                    type="number"
                    inputMode="numeric"
                    value={newCourseId}
                    onChange={(e) => setNewCourseId(e.target.value)}
                    placeholder="e.g. 3320"
                    min={1000}
                    max={9999}
                    required
                  />
                </label>
                <label>
                  Course name
                  <input
                    type="text"
                    value={newCourseName}
                    onChange={(e) => setNewCourseName(e.target.value)}
                    placeholder="e.g. Algorithms and Data Structures"
                    maxLength={200}
                    required
                  />
                </label>
                <p className="add-server-note">
                  New courses are reviewed with the submission before they appear.
                </p>
              </>
            )}

            <label>
              Professor
              <input
                type="text"
                value={professorName}
                onChange={(e) => setProfessorName(e.target.value)}
                placeholder="e.g. Rizk"
                maxLength={100}
                required
              />
            </label>

            <label>
              Discord invite link
              <input
                type="text"
                value={inviteUrl}
                onChange={(e) => setInviteUrl(e.target.value)}
                placeholder="https://discord.gg/…"
                required
              />
            </label>

            {state.status === 'error' && (
              <p className="add-server-error">{state.message}</p>
            )}

            <button
              type="submit"
              className="cta-button"
              disabled={state.status === 'submitting'}
            >
              {state.status === 'submitting' ? 'Checking invite…' : 'Submit for review'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
